/**
 * Internal-drag payload format for moving documents and folders inside the document-management
 * browser. Encoded as JSON in `dataTransfer.setData(DRAG_MIME_TYPE, ...)` so we can identify
 * tourfold internal drags vs. OS file uploads (which use the standard `Files` MIME type).
 *
 * Only fields the drop handler needs to validate eligibility and call the patch endpoint live in
 * here — keep it minimal.
 */
export interface DocumentMgmtDragPayload {
  readonly kind: 'document' | 'folder';
  readonly id: string;
  /** Folder id the source row currently lives in. Used to suppress same-folder drops as a no-op. */
  readonly sourceParentId: string | null;
  /**
   * Materialised path of the source folder (only set when kind = 'folder'). Used by the drop
   * handler to reject "into-descendant" cycles client-side without a server roundtrip.
   */
  readonly sourcePath: string | null;
  /** Display name shown in the confirmation prompt. */
  readonly name: string;
}

/** Custom MIME type carrying our internal-drag payload. */
export const DRAG_MIME_TYPE = 'application/x-tf-doc-mgmt-move';

export function writeDragPayload(dataTransfer: DataTransfer, payload: DocumentMgmtDragPayload): void {
  dataTransfer.setData(DRAG_MIME_TYPE, JSON.stringify(payload));
  // Some browsers require text/plain too for the drag image to render reliably.
  dataTransfer.effectAllowed = 'move';
}

export function readDragPayload(dataTransfer: DataTransfer | null): DocumentMgmtDragPayload | null {
  if (!dataTransfer) return null;
  const raw = dataTransfer.getData(DRAG_MIME_TYPE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DocumentMgmtDragPayload;
  } catch {
    return null;
  }
}

export function dataTransferHasType(dataTransfer: DataTransfer | null, type: string): boolean {
  const types = dataTransfer?.types;
  if (!types) return false;
  const maybeArray = types as unknown as DOMStringList & {
    readonly includes?: (value: string) => boolean;
    readonly contains?: (value: string) => boolean;
  };
  if (typeof maybeArray.includes === 'function') return maybeArray.includes(type);
  if (typeof maybeArray.contains === 'function') return maybeArray.contains(type);
  return Array.from(types as ArrayLike<string>).includes(type);
}

/** True iff the drag carries our internal payload (not an OS file drop). */
export function isInternalDrag(dataTransfer: DataTransfer | null): boolean {
  return dataTransferHasType(dataTransfer, DRAG_MIME_TYPE);
}
